/**
 * Invitation Tracking Zustand Store
 *
 * Global state management for invitation tracking.
 * Handles invitation list, filters, pagination, bulk selection, and stats.
 *
 * Features:
 * - Paginated invitation list with filter support
 * - Aggregated statistics dashboard data
 * - Row selection for bulk operations (resend, remind)
 * - Loading and error state management
 * - Filter updates that trigger automatic re-fetches
 */

import { create } from 'zustand';
import { invitationTrackingApi } from '@shared/api/invitationTrackingApi';
import type {
  InvitationRecord,
  InvitationFilter,
  InvitationStats,
  InvitationAnalytics,
  InvitationCreateRequest,
  BulkOperationResponse,
  EventType,
} from '@shared/types/invitationTracking';

// ============================================================
// DEFAULT FILTER VALUES
// ============================================================

const DEFAULT_FILTERS: InvitationFilter = {
  page: 1,
  per_page: 25,
  sort_by: 'sent_at',
  sort_order: 'desc',
};

// ============================================================
// STORE INTERFACE
// ============================================================

interface InvitationTrackingState {
  // Data
  invitations: InvitationRecord[];
  total: number;
  stats: InvitationStats | null;
  analytics: InvitationAnalytics | null;

  // Filters
  filters: InvitationFilter;

  // UI State
  isLoading: boolean;
  error: string | null;
  selectedIds: Set<number>;

  // Actions - Data fetching
  fetchInvitations: () => Promise<void>;
  fetchStats: (eventType?: EventType) => Promise<void>;
  fetchAnalytics: (eventType?: EventType) => Promise<void>;

  // Actions - CRUD
  createInvitation: (data: InvitationCreateRequest) => Promise<InvitationRecord>;
  deleteInvitation: (id: number) => Promise<void>;

  // Actions - Filters
  updateFilter: (filter: Partial<InvitationFilter>) => void;
  resetFilters: () => void;
  setPage: (page: number) => void;

  // Actions - Selection
  toggleSelection: (id: number) => void;
  selectAll: () => void;
  clearSelection: () => void;

  // Actions - Bulk operations
  bulkResend: (ids: number[]) => Promise<BulkOperationResponse>;
  bulkRemind: (ids: number[], template: string) => Promise<BulkOperationResponse>;
}

// ============================================================
// STORE IMPLEMENTATION
// ============================================================

export const useInvitationTrackingStore = create<InvitationTrackingState>()((set, get) => ({
  // ============================================
  // Initial State
  // ============================================
  invitations: [],
  total: 0,
  stats: null,
  analytics: null,
  filters: { ...DEFAULT_FILTERS },
  isLoading: false,
  error: null,
  selectedIds: new Set<number>(),

  // ============================================
  // Data Fetching
  // ============================================

  /**
   * Fetch invitations using current filters.
   * Manages loading and error states.
   */
  fetchInvitations: async () => {
    set({ isLoading: true, error: null });
    try {
      const { filters } = get();
      const data = await invitationTrackingApi.listInvitations(filters);
      set({
        invitations: data.items,
        total: data.total,
        isLoading: false,
      });
    } catch (error) {
      set({
        isLoading: false,
        error: (error as Error).message,
      });
    }
  },

  /**
   * Fetch aggregated invitation statistics.
   * Optionally filtered by event type.
   */
  fetchStats: async (eventType?: EventType) => {
    try {
      const stats = await invitationTrackingApi.getStats(eventType);
      set({ stats });
    } catch (error) {
      console.error('[InvitationTrackingStore] Failed to fetch stats:', error);
      // Stats are non-critical; do not set global error
    }
  },

  /**
   * Fetch comprehensive invitation analytics with rates.
   * Optionally filtered by event type.
   */
  fetchAnalytics: async (eventType?: EventType) => {
    try {
      const analytics = await invitationTrackingApi.getAnalytics(eventType);
      set({ analytics });
    } catch (error) {
      console.error('[InvitationTrackingStore] Failed to fetch analytics:', error);
      // Analytics are non-critical; do not set global error
    }
  },

  // ============================================
  // CRUD
  // ============================================

  /**
   * Create a new invitation and refresh the list.
   * Returns the created invitation record.
   */
  createInvitation: async (data: InvitationCreateRequest) => {
    set({ isLoading: true, error: null });
    try {
      const record = await invitationTrackingApi.createInvitation(data);
      // Refresh the list to include the new record
      await get().fetchInvitations();
      return record;
    } catch (error) {
      set({
        isLoading: false,
        error: (error as Error).message,
      });
      throw error;
    }
  },

  /**
   * Delete an invitation and refresh the list.
   */
  deleteInvitation: async (id: number) => {
    set({ isLoading: true, error: null });
    try {
      await invitationTrackingApi.deleteInvitation(id);
      // Remove from selection if selected
      const { selectedIds } = get();
      if (selectedIds.has(id)) {
        const next = new Set(selectedIds);
        next.delete(id);
        set({ selectedIds: next });
      }
      // Refresh the list
      await get().fetchInvitations();
    } catch (error) {
      set({
        isLoading: false,
        error: (error as Error).message,
      });
      throw error;
    }
  },

  // ============================================
  // Filters
  // ============================================

  /**
   * Update one or more filter values and re-fetch.
   * Resets to page 1 when filter criteria change (unless page is explicitly set).
   */
  updateFilter: (filter: Partial<InvitationFilter>) => {
    const { filters } = get();
    const isPageChange = 'page' in filter && Object.keys(filter).length === 1;
    const newFilters: InvitationFilter = {
      ...filters,
      ...filter,
      // Reset to page 1 when criteria change (not when only page changes)
      ...(!isPageChange ? { page: 1 } : {}),
    };
    set({ filters: newFilters });
    // Trigger re-fetch with updated filters
    get().fetchInvitations();
  },

  /**
   * Reset all filters to defaults and re-fetch.
   */
  resetFilters: () => {
    set({ filters: { ...DEFAULT_FILTERS }, selectedIds: new Set() });
    get().fetchInvitations();
  },

  /**
   * Convenience method to set the current page and re-fetch.
   */
  setPage: (page: number) => {
    const { filters } = get();
    set({ filters: { ...filters, page } });
    get().fetchInvitations();
  },

  // ============================================
  // Selection
  // ============================================

  /**
   * Toggle selection of a single invitation row.
   */
  toggleSelection: (id: number) => {
    const { selectedIds } = get();
    const next = new Set(selectedIds);
    if (next.has(id)) {
      next.delete(id);
    } else {
      next.add(id);
    }
    set({ selectedIds: next });
  },

  /**
   * Select all invitations currently in the list.
   */
  selectAll: () => {
    const { invitations } = get();
    const allIds = new Set(invitations.map((inv) => inv.id));
    set({ selectedIds: allIds });
  },

  /**
   * Clear all selections.
   */
  clearSelection: () => {
    set({ selectedIds: new Set() });
  },

  // ============================================
  // Bulk Operations
  // ============================================

  /**
   * Bulk resend invitations. Clears selection and refreshes list on success.
   */
  bulkResend: async (ids: number[]) => {
    set({ isLoading: true, error: null });
    try {
      const result = await invitationTrackingApi.bulkResend(ids);
      set({ selectedIds: new Set() });
      // Refresh to reflect updated statuses
      await get().fetchInvitations();
      return result;
    } catch (error) {
      set({
        isLoading: false,
        error: (error as Error).message,
      });
      throw error;
    }
  },

  /**
   * Bulk send reminders. Clears selection and refreshes list on success.
   */
  bulkRemind: async (ids: number[], template: string) => {
    set({ isLoading: true, error: null });
    try {
      const result = await invitationTrackingApi.bulkRemind(ids, template);
      set({ selectedIds: new Set() });
      // Refresh to reflect updated statuses
      await get().fetchInvitations();
      return result;
    } catch (error) {
      set({
        isLoading: false,
        error: (error as Error).message,
      });
      throw error;
    }
  },
}));
